/**
 * Geometrie der Torte („Standardmangel per Torte“, Paket D) — reine
 * Funktionen ohne React.
 *
 * viewBox 200 × 200, Mitte (100, 100), Kreisring zwischen R_INNEN 30 und
 * R_AUSSEN 98, 9 Stücke à 40°. Stück 0 liegt oben zentriert (−20° … +20°),
 * die weiteren folgen im Uhrzeigersinn; Stück 8 (~320°, etwa 10:40 Uhr) ist
 * „Sonstiges“. Winkel zählen ab 12 Uhr im Uhrzeigersinn, SVG-y wächst nach
 * unten.
 */

export const VIEWBOX = 200;
export const MITTE_X = 100;
export const MITTE_Y = 100;
export const R_AUSSEN = 98;
export const R_INNEN = 30;
/** Mittelknopf: Durchmesser 52 Einheiten ≈ 87 px bei 336 px Torte. */
export const R_MITTE = 26;
export const STUECKE = 9;
export const STUECK_WINKEL = 360 / STUECKE;
/** Standard-Spalt zwischen zwei Stücken (Einheiten der viewBox, gleich breit von innen bis außen). */
export const LUECKE = 1.2;

/** Auf 3 Nachkommastellen, ohne „-0“ — stabile Pfad-Texte für Tests und SSR. */
const rund = (n: number): number => Math.round(n * 1000) / 1000 + 0;

const bogenmass = (grad: number): number => (grad * Math.PI) / 180;

/** Punkt auf dem Kreis um die Mitte; 0° = oben, im Uhrzeigersinn. */
export function punktAufKreis(winkelGrad: number, r: number): { x: number; y: number } {
    const w = bogenmass(winkelGrad);
    return { x: rund(MITTE_X + r * Math.sin(w)), y: rund(MITTE_Y - r * Math.cos(w)) };
}

/** Stückindex auf 0 … 8 gebracht (Unsinn → 0). */
function index9(index: number): number {
    const i = Number.isFinite(index) ? Math.floor(index) : 0;
    return ((i % STUECKE) + STUECKE) % STUECKE;
}

/** Mittelwinkel eines Stücks in Grad (0, 40, 80 … 320). */
export function stueckWinkel(index: number): number {
    return index9(index) * STUECK_WINKEL;
}

/**
 * SVG-Pfad eines Kreisringsegments. `luecke` ist die Breite des Spalts zu den
 * Nachbarn (je Seite die Hälfte) und bleibt von innen bis außen gleich breit.
 */
export function stueckPfad(index: number, luecke: number = LUECKE): string {
    const mitte = stueckWinkel(index);
    const halb = Number.isFinite(luecke) && luecke > 0 ? luecke / 2 : 0;
    // Winkel, der am jeweiligen Radius der halben Spaltbreite entspricht.
    const versatz = (r: number): number => (Math.asin(Math.min(1, halb / r)) * 180) / Math.PI;
    const dA = versatz(R_AUSSEN);
    const dI = versatz(R_INNEN);
    const von = mitte - STUECK_WINKEL / 2;
    const bis = mitte + STUECK_WINKEL / 2;
    const a1 = punktAufKreis(von + dA, R_AUSSEN);
    const a2 = punktAufKreis(bis - dA, R_AUSSEN);
    const i1 = punktAufKreis(bis - dI, R_INNEN);
    const i2 = punktAufKreis(von + dI, R_INNEN);
    return [
        `M ${a1.x} ${a1.y}`,
        `A ${R_AUSSEN} ${R_AUSSEN} 0 0 1 ${a2.x} ${a2.y}`,
        `L ${i1.x} ${i1.y}`,
        `A ${R_INNEN} ${R_INNEN} 0 0 0 ${i2.x} ${i2.y}`,
        'Z',
    ].join(' ');
}

/** Mittelpunkt eines Stücks auf Radius `r` (Standard: Mitte des Rings). */
export function stueckMitte(index: number, r: number = (R_AUSSEN + R_INNEN) / 2): { x: number; y: number } {
    return punktAufKreis(stueckWinkel(index), r);
}

/**
 * Welches Stück liegt unter dem Punkt (Koordinaten der viewBox)? In der
 * Mitte (innerhalb R_INNEN) und außerhalb von R_AUSSEN: null.
 */
export function stueckAnPunkt(x: number, y: number): number | null {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    const dx = x - MITTE_X;
    const dy = y - MITTE_Y;
    const abstand = Math.hypot(dx, dy);
    if (abstand < R_INNEN || abstand > R_AUSSEN) return null;
    const winkel = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const verschoben = (winkel + 360 + STUECK_WINKEL / 2) % 360;
    return Math.floor(verschoben / STUECK_WINKEL) % STUECKE;
}
